import { useClickAway } from 'ahooks';
import classNames from 'classnames';
import { all, always, append, assoc, assocPath, both, complement, compose, equals, filter, find, gt, is, isEmpty, length, map, path, prop, propEq, propOr, reduce, remove, update, when, any } from 'ramda';
import { notEqual, round } from 'ramda-adjunct';
import React, { useEffect, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { createNewElement, droppingItem, iStringToNumber } from '../helpers/elements';
import { editorWidth, getSizeElement, updateLayout, widthOneGrid, withEditing } from '../helpers/widget';
import { BASE_FRAME_STYLE } from '../../../../_CONST';
import DroppedCard from './DroppedCard';
import './layout.css';

const DropZone = ({
  values,
  setState,
  setYScrollBarPosition,
  isDownNbColsGrid,
  widgetIsEditable,
  elements,
  optionsElement,
  widgetMinSize,
  customColors,
  customStyles,
  defaultFont,
  isFocus,
  setIsFocus,
  useCSSTransforms,
  setUseCSSTransforms,
  onCreateStyle,
  isOpenCreateGlobalVariableModal,
  isFrameConditionedElements,
  toolbarRef,
  isToolbarFixed,
  isLateEdit,
  scale,
  addToHistory,
  layout,
  setLayout,
  isUndoPreviousEditorState,
  setIsUndoPreviousEditorState,
  historyActions,
  addActionOnPrevious
}) => {
  const ref = useRef(null);
  const [isHoveredOver, setIsHoveredOver] = useState([]);
  const [currentAction, setCurrentAction] = useState(null);
  const { contents, nbColsGrid, heightRowsGrid, orientation } = values;

  const [{ isOver, canDrop, itemDrag }, drop] = useDrop(
    () => ({
      accept: 'CARD',
      drop: (element, monitor) => {
        const position = monitor.getClientOffset();
        const bounds = prop('current', ref).getBoundingClientRect();
        const fixedY = prop('y', position) - prop('y', bounds);
        const newElement = createNewElement(values, element, fixedY, elements, orientation);
        addNewElement(newElement);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
        itemDrag: monitor.getItem()
      })
    }),
    [contents, orientation]
  );

  const isActive = isOver && canDrop;
  const widgetIsEmpty = !contents.length && !layout.length;

  if (!widgetIsEmpty) setYScrollBarPosition(propOr('', 'clientHeight', ref.current));

  useEffect(() => {
    setIsHoveredOver(contents.map(() => false));
    setIsFocus(contents.map(() => false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gt(contents.length, layout.length)) updateLayout(contents, nbColsGrid, heightRowsGrid, setLayout, widgetIsEditable, isDownNbColsGrid, elements, widgetMinSize, orientation);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contents.length]);

  useEffect(() => {
    updateLayout(contents, nbColsGrid, heightRowsGrid, setLayout, widgetIsEditable, isDownNbColsGrid, elements, widgetMinSize, orientation);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nbColsGrid, heightRowsGrid, orientation]);

  const addNewElement = (newElement, newLayout) => {
    let newContents = [...contents, newElement];

    if (newLayout) {
      map((l) => {
        const newPosition = { x: prop('x', l) * widthOneGrid(nbColsGrid, orientation), y: prop('y', l) * heightRowsGrid };
        const oldPosition = path(['contents', iStringToNumber(prop('i', l)), 'position'], values);
        if (notEqual(oldPosition, newPosition)) {
          newContents = assocPath([iStringToNumber(prop('i', l)), 'position'], newPosition, newContents);
        }
      })(newLayout);
    }

    setState(prevState => withEditing(
      assocPath(['values', 'contents'], newContents, prevState)
    ));

    addToHistory(contents, newContents, 'addNewElement');

    setIsHoveredOver(append(true, map(always(false), contents)));
    setIsFocus(append(true, map(always(false), contents)));
    setUseCSSTransforms(false);
  };

  const handleDelete = (id) => {
    const newContents = remove(id, 1, contents);
    setState(prevState => {
      let newState = withEditing(
        assocPath(['values', 'contents'], newContents, prevState)
      );
      if (isEmpty(newContents)) {
        newState = assocPath(['values', 'frameStyle'], BASE_FRAME_STYLE, newState);
      }
      addToHistory(path(['values', 'contents'], prevState), path(['values', 'contents'], newState), 'onDeleteElement');
      return newState;
    });
    setLayout([]);
    setIsHoveredOver(newContents);
    setIsFocus(newContents);
    setUseCSSTransforms(true);
  };

  const onLayoutChange = (l) => {
    const updatedContents = reduce((acc, item) => {
      const newPosition = {
        x: prop('x', item) * widthOneGrid(nbColsGrid, orientation),
        y: prop('y', item) * heightRowsGrid
      };
      const newSize = {
        width: prop('w', item) === nbColsGrid ? editorWidth(orientation) : widthOneGrid(nbColsGrid, orientation) * prop('w', item),
        height: prop('h', item) * heightRowsGrid
      };
      const isValidNumber = both(is(Number), complement(equals(NaN)));
      const index = iStringToNumber(item.i);
      if (isValidNumber(index)) {
        const nextContents = compose(
          assocPath([index, 'position'], newPosition),
          assocPath([index, 'size'], newSize)
        )(acc);
        
        return nextContents;
      }
      return acc;
    }, [...contents], l);

    setState(assocPath(['values', 'contents'], updatedContents));

    if (any(equals(currentAction), ['resize', 'drag'])) {
      addToHistory(contents, updatedContents, currentAction);
      setCurrentAction(null);
    }

    if (all(notEqual('dropping-item'))(map(prop('i'))(l))) {
      const newLayout = map((item) => ({ ...item, i: iStringToNumber(item.i) }))(l);
      setLayout(newLayout);
    }
  };

  const onDropItem = (newLayout, item) => {
    const fixedY = prop('y', item) * heightRowsGrid;
    const newElement = createNewElement(values, itemDrag, fixedY, elements);
    addNewElement(newElement, filter(l => l !== item, newLayout));
  };

  const onDuplicateElement = (id) => {
    const positionY = reduce(
      (acc, item) => Math.max(acc, item.y + item.h),
      0,
      layout
    );
    const elementToDuplicate = find(propEq(id, 'i'), layout);
    const newLayoutItem = compose(
      assoc('i', length(layout)),
      assoc('y', positionY)
    )(elementToDuplicate);
    setLayout([...layout, newLayoutItem]);
    const newContent = compose(
      assocPath(['position', 'x'], prop('x', elementToDuplicate)),
      assocPath(['position', 'y'], positionY * heightRowsGrid)
    )(contents[id]);
    setState(
      assocPath(['values', 'contents'], append(newContent, contents))
    );
    addToHistory(contents, [...contents, newContent], 'onDuplicateElement');
    setIsHoveredOver([...contents.map(() => false), true]);
    setIsFocus([...contents.map(() => false), true]);
    setUseCSSTransforms(false);
  };

  useClickAway(() => {
    const removeFocus = !isOpenCreateGlobalVariableModal && (!window.getSelection() || window.getSelection().isCollapsed);
    if (removeFocus) {
      setIsFocus(map(() => false));
      setUseCSSTransforms(true);
    }
  }, [
    ref,
    toolbarRef.current,
    document.getElementById('style-bar'),
    document.getElementById('variables-library-bar'),
    document.getElementById('element-rules-bar'),
    ...document.getElementsByClassName('ant-dropdown-menu'),
    ...document.getElementsByClassName('ant-dropdown'),
    ...document.getElementsByClassName('modal'),
    ...document.getElementsByClassName('ant-select-dropdown'),
    ...document.getElementsByClassName('anticon')
  ]);

  const onChangeContentHeight = (id) => (contentHeight) => {
    const newHeight = round(contentHeight / heightRowsGrid);
    setState(assocPath(['values', 'contents', id, 'size', 'height'], contentHeight));
    const newLayout = map((l) =>
      when(propEq(id, 'i'), assoc('h', newHeight))(l))(layout);
    setLayout(newLayout);
  };

  const actualWidthWidget = reduce((maxWidth, element) => {
    const itemX = path(['position', 'x'], element);
    const itemWidth = getSizeElement('width', element);
    const widthElement = itemX + itemWidth;
    if (widthElement > maxWidth) {
      return widthElement;
    }

    return maxWidth;
  }, 0, contents);

  const getBorderFrameStyle = (position) => {
    const borderWidth = path(['frameStyle', 'borderWidth', position], values);
    const borderStyle = path(['frameStyle', 'borderStyle', position], values);
    const borderColor = path(['frameStyle', 'borderColor', position], values);
    return `${borderWidth}px ${borderStyle} ${borderColor}`;
  };

  const frameStyle = {
    width: `${actualWidthWidget}px`,
    backgroundColor: path(['frameStyle', 'backgroundColor'], values),
    borderRight: getBorderFrameStyle('right'),
    borderLeft: getBorderFrameStyle('left'),
    borderTop: getBorderFrameStyle('top'),
    borderBottom: getBorderFrameStyle('bottom'),
    borderRadius: `${path(['frameStyle', 'borderRadius', 'topLeft'], values)}px ${path(['frameStyle', 'borderRadius', 'topRight'], values)}px ${path(['frameStyle', 'borderRadius', 'bottomRight'], values)}px ${path(['frameStyle', 'borderRadius', 'bottomLeft'], values)}px`
  };

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  return (
    <div ref={drop} role="DropZone" className={classNames('w-full h-full shadow-none', { 'bg-flexi-lightblue bg-opacity-20': isActive && !layout.length }, { 'py-0.5': !layout.length })}>
      <div ref={ref} className={classNames('w-full h-full shadow-none relative')}>
        {widgetIsEmpty && (
          <div className={classNames('flex justify-center m-1 p-2 border-2 border-dashed text-inherit', { 'text-flexi-text border-transparent': isActive && !layout.length }, { 'border-flexi-border text-flexi-text': !isActive && canDrop }, { 'border-gray-200': !isActive && !canDrop })}>
            {!isActive ? 'Glisser un élément' : 'Vous pouvez lâcher votre élément'}
          </div>
        )}
        {/* eslint-disable-next-line no-restricted-syntax */}
        <div className="frame-style absolute top-0 left-0 h-full" style={frameStyle} />
        <GridLayout
          cols={nbColsGrid}
          rowHeight={heightRowsGrid}
          width={editorWidth(orientation)}
          autoSize
          measureBeforeMount={true}
          padding={[0, 0]}
          margin={[0, 0]}
          useCSSTransforms={useCSSTransforms}
          allowOverlap={false}
          className="relative transition-height duration-200 ease"
          layout={layout}
          onLayoutChange={onLayoutChange}
          onDragStop={() => {
            setState(withEditing);
            setCurrentAction('drag');
          }}
          onResizeStop={() => {
            setState(withEditing);
            setCurrentAction('resize');
          }}
          draggableCancel=".not-draggable"
          draggableHandle=".item-grid"
          isDroppable={widgetIsEditable}
          onDrop={(l, item) => onDropItem(l, item)}
          droppingItem={droppingItem(nbColsGrid)}
          transformScale={isSafari ? null : scale}
        >
          {layout.map((l) => {
            const elementContent = path(['contents', prop('i', l)], values);
            if (equals('childrens', prop('type', elementContent))) elementContent.type = 'children';
            if (!elementContent) return null;
            const containerClassName = classNames(
              { 'ring-1 ring-inset ring-flexi-primary-color not-draggable': isFocus[prop('i', l)] },
              { 'ring-1 ring-inset ring-black': isHoveredOver[prop('i', l)] && !isFocus[prop('i', l)] },
              'item-grid z-auto'
            );
            return (
              <div
                key={prop('i', l)}
                data-grid={l}
                onMouseOver={() => setIsHoveredOver(update(prop('i', l), true))}
                onFocus={() => setIsHoveredOver(update(prop('i', l), true))}
                onMouseOut={() => setIsHoveredOver(update(prop('i', l), false))}
                onBlur={() => setIsHoveredOver(update(prop('i', l), false))}
                className={containerClassName}
                onClick={() => {
                  setIsFocus(compose(update(prop('i', l), true), map(() => false)));
                  setUseCSSTransforms(false);
                }}
              >
                <DroppedCard
                  widgetIsEditable={widgetIsEditable}
                  values={values}
                  setState={setState}
                  id={prop('i', l)}
                  type={propOr('', 'type', elementContent)}
                  handleDelete={handleDelete}
                  renderToolbar="#element-toolbar"
                  renderToolbarOld="#element-toolbar-old"
                  renderVariable="#element-variable-bar"
                  isFocus={isFocus[prop('i', l)]}
                  optionsElement={optionsElement}
                  onChangeContentHeight={onChangeContentHeight(prop('i', l))}
                  defaultFont={defaultFont}
                  customColors={customColors}
                  customStyles={customStyles}
                  orientation={orientation}
                  onCreateStyle={onCreateStyle}
                  isFrameConditionedElements={isFrameConditionedElements}
                  onDuplicateElement={onDuplicateElement}
                  isToolbarFixed={isToolbarFixed}
                  isLateEdit={isLateEdit}
                  addToHistory={addToHistory}
                  isUndoPreviousEditorState={isUndoPreviousEditorState}
                  setIsUndoPreviousEditorState={setIsUndoPreviousEditorState}
                  historyActions={historyActions}
                  addActionOnPrevious={addActionOnPrevious}
                />
              </div>
            );
          })}
        </GridLayout>
      </div>
    </div>
  );
};

export default DropZone;
